import React, { forwardRef, useId } from 'react';

/**
 * Reusable labeled text input with an optional leading icon, trailing
 * adornment (e.g. a show/hide password button) and error message.
 */
export const TextField = forwardRef(function TextField(
  {
    label,
    id,
    icon: Icon,
    trailing,
    error,
    className = '',
    containerClassName = '',
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = id || generatedId;

  return (
    <div className={`flex flex-col gap-1.5 ${containerClassName}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-medium uppercase tracking-wide text-brand-500"
        >
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {Icon && (
          <Icon
            size={18}
            className="pointer-events-none absolute left-3.5 text-slate-400 peer-focus:text-brand-500"
          />
        )}
        <input
          ref={ref}
          id={inputId}
          className={[
            'peer w-full rounded-xl border-2 border-brand-200 bg-slate-50 py-3 text-sm font-medium text-slate-800',
            'transition-all duration-200 placeholder:text-slate-400 placeholder:font-normal',
            'focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/15',
            Icon ? 'pl-11' : 'pl-4',
            trailing ? 'pr-11' : 'pr-4',
            error
              ? 'border-accent-300 focus:border-accent-400'
              : 'border-slate-200 hover:border-slate-300 focus:border-brand-500',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          ].join(' ')}
          aria-invalid={!!error}
          {...props}
        />
        {trailing && (
          <div className="absolute right-2.5 flex items-center">{trailing}</div>
        )}
      </div>
      {error && <p className="text-xs font-medium text-accent-600">{error}</p>}
    </div>
  );
});

export default TextField;
